import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { leetcodeUrlSchema } from '@/lib/audition/steps'
import type { SubmissionAddress } from '@/lib/audition/submission-address'
import { trpc } from '@/lib/trpc'

interface LeetcodeStepProps {
  address: SubmissionAddress
  initialUrl: string | null
  initialNotes: string | null
  onSkip: () => void
  onSaved: () => void
  /**
   * A panel inside the done screen's boost hub (under that hub's h2), so the
   * level travels with the call site rather than claiming a nesting that
   * isn't there.
   */
  headingLevel: 2 | 3
}

/**
 * The done screen's LeetCode panel (LG-118), for an applicant who left the
 * link blank on Background — where the wizard asks for it since LG-1053 —
 * and wants to add it after submitting. Also the only place the notes are
 * asked for.
 *
 * The link validates through `leetcodeUrlSchema`, the same schema Background
 * and the server use, so the two boxes cannot disagree about what a profile
 * is.
 *
 * Copy here is placeholder pending checkpoint §8.4. It must never say
 * "audition" — that word is internal vocabulary only.
 */
export function LeetcodeStep({
  address,
  initialUrl,
  initialNotes,
  onSkip,
  onSaved,
  headingLevel,
}: LeetcodeStepProps) {
  const Heading = `h${headingLevel}` as const
  const [url, setUrl] = useState(initialUrl ?? '')
  const [notes, setNotes] = useState(initialNotes ?? '')
  const [error, setError] = useState<string | null>(null)
  const save = trpc.audition.saveLeetcode.useMutation()

  function submit() {
    // The two fields are independent (2026-08-30): notes save without a
    // link, and an empty link is the explicit take-down of a shared one.
    // The server's own schema, so a link it would refuse is refused here in
    // its own sentence rather than as an opaque 400.
    const link = leetcodeUrlSchema.safeParse(url)
    if (!link.success) {
      setError(link.error.issues.at(0)?.message ?? null)
      return
    }

    setError(null)
    save.mutate(
      {
        ...address,
        leetcodeUrl: link.data,
        leetcodeNotes: notes.trim() || undefined,
      },
      {
        // Per call, not on the hook: TanStack runs a hook's callbacks even
        // after the step unmounts, and "Skip for now" stays live while a save
        // is pending. An answer landing after the applicant has moved on is no
        // longer this step's to act on — by then they may have restarted under
        // a new token, which a second reset would throw away and a late
        // "saved" would strand on the account step with no token.
        onSuccess: onSaved,
      },
    )
  }

  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <Heading className="text-xl font-semibold">
          Want to show us your problem-solving history?
        </Heading>
        <p className="text-sm text-muted-foreground">
          Optional. If you practise on LeetCode, a link helps us see where
          you're strong and where we'd focus. You can skip this and add it
          later.
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium" htmlFor="leetcode-url">
          LeetCode profile link
        </label>
        <Input
          id="leetcode-url"
          value={url}
          placeholder="leetcode.com/u/your-handle"
          onChange={(e) => setUrl(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium" htmlFor="leetcode-notes">
          Anything you want us to know about it?
        </label>
        <textarea
          id="leetcode-notes"
          className="h-24 w-full rounded-md border p-2 text-sm"
          value={notes}
          placeholder="Optional — e.g. which topics you've focused on."
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      {/* One sentence at a time: a link refused here sent nothing, so the
          last attempt's "couldn't save" must not stand beside it. */}
      {(error || save.isError) && (
        <p role="alert" className="text-sm text-destructive">
          {error ?? "We couldn't save that — please try again."}
        </p>
      )}

      <div className="flex items-center gap-3">
        <Button type="button" onClick={submit} disabled={save.isPending}>
          Save and continue
        </Button>
        {/* Quiet, but a real button: an optional step that is hard to leave
            stops being optional. */}
        <button
          type="button"
          className="text-sm text-muted-foreground underline"
          onClick={onSkip}
        >
          Skip for now
        </button>
      </div>
    </section>
  )
}
